/**
 * My Tasks grouping and sorting (the home screen).
 *
 * Groups: Overdue · Today · Next (within `nextDays`) · Later (further out) · Unscheduled.
 * Date-only tasks become overdue the day *after* their due date, never at a time of day.
 */
import { diffDays, localDateTimeToInstant, todayInTz } from './time.ts';
import type { ISODate, ISOTime, Instant, Priority, StatusType } from './types.ts';

export type MyTasksGroupKey = 'today' | 'overdue' | 'next' | 'later' | 'unscheduled';

/** Display order of groups (matches ClickUp: Today first, then Overdue). */
export const MY_TASKS_GROUP_ORDER: readonly MyTasksGroupKey[] = [
  'today',
  'overdue',
  'next',
  'later',
  'unscheduled',
];

export const MY_TASKS_GROUP_LABEL: Record<MyTasksGroupKey, string> = {
  today: 'Today',
  overdue: 'Overdue',
  next: 'Next',
  later: 'Later',
  unscheduled: 'Unscheduled',
};

export interface GroupingContext {
  now: Date;
  tz: string;
  /** Days ahead (after today) that count as "Next". */
  nextDays: number;
}

export interface DueLike {
  due_date: ISODate | null;
  due_time: ISOTime | null;
  due_at?: Instant | null;
}

function timedDueInstant(task: DueLike, tz: string): number | null {
  if (!task.due_date || !task.due_time) return null;
  if (task.due_at) return Date.parse(task.due_at);
  return localDateTimeToInstant(task.due_date, task.due_time, tz).getTime();
}

export function isOverdue(task: DueLike, ctx: Pick<GroupingContext, 'now' | 'tz'>): boolean {
  if (!task.due_date) return false;
  const timed = timedDueInstant(task, ctx.tz);
  if (timed !== null) return timed < ctx.now.getTime();
  return task.due_date < todayInTz(ctx.tz, ctx.now);
}

export function myTasksGroupOf(task: DueLike, ctx: GroupingContext): MyTasksGroupKey {
  if (!task.due_date) return 'unscheduled';
  if (isOverdue(task, ctx)) return 'overdue';
  const days = diffDays(task.due_date, todayInTz(ctx.tz, ctx.now));
  if (days <= 0) return 'today';
  return days <= ctx.nextDays ? 'next' : 'later';
}

export type TaskSortKey = 'status' | 'due_date' | 'created' | 'updated' | 'priority' | 'manual';
export type SortDirection = 'asc' | 'desc';

export interface SortableTask extends DueLike {
  id: string;
  name: string;
  priority: Priority | null;
  created_at: Instant;
  updated_at: Instant;
  position: number;
  completed_at?: Instant | null;
}

export interface SortContext {
  tz: string;
  /** Position of the task's status in display order, for sorting by status. */
  statusRank?: (task: SortableTask) => number;
}

const NULLS_LAST = Number.POSITIVE_INFINITY;

function dueSortValue(task: DueLike, tz: string): number {
  if (!task.due_date) return NULLS_LAST;
  const timed = timedDueInstant(task, tz);
  if (timed !== null) return timed;
  // Date-only tasks sort after timed tasks on the same day.
  return localDateTimeToInstant(task.due_date, '23:59:59', tz).getTime();
}

export function compareTasks(
  a: SortableTask,
  b: SortableTask,
  key: TaskSortKey,
  direction: SortDirection,
  ctx: SortContext,
): number {
  const dir = direction === 'asc' ? 1 : -1;
  let cmp = 0;
  switch (key) {
    case 'due_date': {
      const av = dueSortValue(a, ctx.tz);
      const bv = dueSortValue(b, ctx.tz);
      // Tasks without a date always go last, whatever the direction.
      if (av === NULLS_LAST || bv === NULLS_LAST) {
        cmp = av === bv ? 0 : av === NULLS_LAST ? 1 : -1;
        return cmp || a.position - b.position;
      }
      cmp = (av - bv) * dir;
      break;
    }
    case 'priority': {
      const av = a.priority ?? 5;
      const bv = b.priority ?? 5;
      if (av === 5 || bv === 5) {
        cmp = av === bv ? 0 : av === 5 ? 1 : -1;
        return cmp || a.position - b.position;
      }
      cmp = (av - bv) * dir;
      break;
    }
    case 'status':
      cmp = ((ctx.statusRank?.(a) ?? 0) - (ctx.statusRank?.(b) ?? 0)) * dir;
      break;
    case 'created':
      cmp = (Date.parse(a.created_at) - Date.parse(b.created_at)) * dir;
      break;
    case 'updated':
      cmp = (Date.parse(a.updated_at) - Date.parse(b.updated_at)) * dir;
      break;
    case 'manual':
      cmp = (a.position - b.position) * dir;
      break;
  }
  return cmp || a.position - b.position || a.name.localeCompare(b.name);
}

export interface GroupSort {
  key: TaskSortKey;
  direction: SortDirection;
}

export const DEFAULT_GROUP_SORT: GroupSort = { key: 'due_date', direction: 'asc' };

export interface MyTasksInput extends SortableTask {
  status_type: StatusType;
  deleted_at?: Instant | null;
  archived_at?: Instant | null;
}

/** Split open tasks into My Tasks groups, each sorted by its own sort setting. */
export function groupMyTasks<T extends MyTasksInput>(
  tasks: readonly T[],
  ctx: GroupingContext & Omit<SortContext, 'tz'>,
  sorts: Partial<Record<MyTasksGroupKey, GroupSort>> = {},
): Record<MyTasksGroupKey, T[]> {
  const groups: Record<MyTasksGroupKey, T[]> = {
    today: [],
    overdue: [],
    next: [],
    later: [],
    unscheduled: [],
  };
  for (const t of tasks) {
    if (t.deleted_at || t.archived_at) continue;
    if (t.status_type === 'done' || t.status_type === 'closed') continue;
    groups[myTasksGroupOf(t, ctx)].push(t);
  }
  for (const key of MY_TASKS_GROUP_ORDER) {
    const sort = sorts[key] ?? DEFAULT_GROUP_SORT;
    groups[key].sort((a, b) =>
      compareTasks(a, b, sort.key, sort.direction, { tz: ctx.tz, statusRank: ctx.statusRank }),
    );
  }
  return groups;
}

/** Completed tasks for the Done tab, newest completion first. */
export function doneTasks<T extends MyTasksInput>(tasks: readonly T[]): T[] {
  return tasks
    .filter(
      (t) =>
        !t.deleted_at && !t.archived_at && (t.status_type === 'done' || t.status_type === 'closed'),
    )
    .sort(
      (a, b) =>
        Date.parse(b.completed_at ?? b.updated_at) - Date.parse(a.completed_at ?? a.updated_at),
    );
}

/** The due date a task created from a group's "+" gets. */
export function defaultDueDateForGroup(
  group: MyTasksGroupKey,
  ctx: Pick<GroupingContext, 'now' | 'tz'>,
): ISODate | null {
  const today = todayInTz(ctx.tz, ctx.now);
  switch (group) {
    case 'today':
    case 'overdue':
      return today;
    case 'next':
    case 'later': {
      const d = new Date(`${today}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() + 1);
      return d.toISOString().slice(0, 10);
    }
    case 'unscheduled':
      return null;
  }
}
